import { useTranslation } from 'react-i18next';
import { useAreasQuery, useIterationsQuery, useMeQuery, useTagsQuery, useViewsQuery } from '@platform/state/api';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { appRoutes } from '../../shell/appRoutes';
import { KpiTile } from '../system/KpiTile';
import { SectionCard } from '../system/SectionCard';
import { SysNote } from '../system/SysNote';
import { SystemPage } from '../system/SystemPage';
import { useCustomizationScope } from '../system/useCustomizationScope';
import { linkTypeInverse } from './linkTypeKeys';
import { useGlobalKpis } from './useGlobalKpis';
import { typeStats } from './typeStats';
import { useWorkspaceDefinitions } from './useWorkspaceDefinitions';

const WS_SECTIONS: Array<[string, string]> = [['modules', 'installed'], ['forms', 'forms'], ['builder', 'builder'], ['layout', 'layout'], ['areas', 'areas'], ['iterations', 'iterations'], ['tags', 'tags'], ['links', 'links']];
const GLOBAL_SECTIONS: Array<[string, string]> = [['modules', 'catalog'], ['forms', 'formsGlobal'], ['layout', 'layoutGlobal'], ['links', 'links']];

/**
 * Огляд «Налаштування» (прототип v4 G:L8391–8441, pgCust; SP-02/03): заголовок «Налаштування · простір» або «· Глобально» з описом рівня;
 * без прав — «Ви можете переглядати сторінку, але…»; плитки — у просторі «Модулі · Поля · Шляхи напрямів · Шляхи ітерацій · Теги · Збережені
 * подання» («Модулі» й «Поля» — як у прототипі: реалізовані типи записів встановлених модулів і їхні поля), глобально — «Шаблони модулів ·
 * Глобальні форми · Робочі простори · Типи зв’язків» (глобальний каталог ADR-027 і всі простори платформи; «N з власним розміщенням полів» —
 * кількість сервера); «Розділи» — картки розділів рівня.
 */
export function CustomizeHomePage() {
  const { t } = useTranslation();
  const { ws } = useWorkspaceParams();
  const { data: me } = useMeQuery();
  const scope = useCustomizationScope(ws);
  const global = scope.scope === 'global';
  const { catalog, definitions } = useWorkspaceDefinitions(ws, global);
  const { data: areas } = useAreasQuery(ws, { skip: !ws || global });
  const { data: iterations } = useIterationsQuery(ws, { skip: !ws || global });
  const { data: tags } = useTagsQuery(ws, { skip: !ws || global });
  const { data: views } = useViewsQuery({ ws }, { skip: !ws || global });
  const g = useGlobalKpis(!global || !scope.canGlobal);
  const wsName = me?.workspaces.find((w) => w.id === ws)?.name ?? '';
  // Вимкнені модулі простору (реєстри Ради поза парламентом — не модулі простору, як у прототипі, тож не рахуються)
  const off = catalog?.modules.filter((m) => !m.installed && !m.registry).length ?? 0;
  const stats = definitions && typeStats(definitions);
  const n = (v: number | undefined) => (v === undefined ? '…' : String(v));
  const at = (section: string) => appRoutes.customize(section);
  // «N з власним розміщенням полів» — число сервера (глобальний каталог); поки його немає, примітки немає
  const ownLayout = g.ownLayout === undefined ? undefined : t('sysPages.custHome.ownLayout', { count: g.ownLayout });
  const kpis = global ? [
    { key: 'templates', label: t('sysPages.custHome.templates'), value: n(g.templates), note: t('sysPages.custHome.disabled', { count: g.disabled }), to: at('modules') },
    { key: 'forms', label: t('sysPages.custHome.globalForms'), value: n(g.forms), note: t('sysPages.custHome.customized'), to: at('forms') },
    { key: 'ws', label: t('sysPages.sections.workspaces'), value: n(g.workspaces ?? me?.workspaces.length), note: ownLayout, to: appRoutes.admin('workspaces') },
    { key: 'links', label: t('sysPages.sections.links'), value: String(Object.keys(linkTypeInverse).length), note: t('sysPages.custHome.platformWide'), to: at('links') },
  ] : [
    { key: 'modules', label: t('sysPages.sections.modules'), value: n(stats?.types), note: t('sysPages.custHome.disabled', { count: off }), to: at('modules') },
    { key: 'fields', label: t('sysPages.custHome.fields'), value: n(stats?.fields), note: t('sysPages.custHome.fieldsNote'), to: at('modules') },
    { key: 'areas', label: t('sysPages.sections.areas'), value: n(areas?.length), to: at('areas') },
    { key: 'iterations', label: t('sysPages.sections.iterations'), value: n(iterations?.length), to: at('iterations') },
    { key: 'tags', label: t('sysPages.sections.tags'), value: n(tags?.length), to: at('tags') },
    { key: 'views', label: t('sysPages.custHome.views'), value: n(views?.length), note: t('sysPages.custHome.viewsNote'), to: at('modules') },
  ];
  return (
    <SystemPage title={global ? t('sysPages.custHome.titleGlobal') : t('sysPages.custHome.titleWs', { ws: wsName })} sub={t(global ? 'sysPages.custHome.subGlobal' : 'sysPages.custHome.subWs')}>
      {scope.noRights && <SysNote tone="alert">{t('sysPages.custHome.noRights')}</SysNote>}
      <div className="sp-kpis">{kpis.map(({ key, ...k }) => <KpiTile key={key} {...k} />)}</div>
      <div className="sp-block">
        <div className="sp-eyebrow">{t('sysPages.adminHome.sections')}</div>
        <div className="sp-cards">
          {(global ? GLOBAL_SECTIONS : WS_SECTIONS).map(([route, key]) => (
            <SectionCard key={route} title={t(`sysPages.sections.${route === 'modules' ? key : route}`)} desc={t(`sysPages.sectionDesc.${key}`)} to={at(route)} />
          ))}
        </div>
      </div>
    </SystemPage>
  );
}
